import { type SyntheticEvent, useCallback } from "react";

import { Metadata } from "@doitintl/cmp-models";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import { Divider, IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";
import capitalize from "lodash/capitalize";

import { reportText } from "../../../../assets/texts";
import { metricSplittingTxt } from "../../../../assets/texts/CloudAnalytics";
import { useCustomerId } from "../../../../Components/hooks/useCustomerId";
import { type Split } from "../../../../Components/SplitCostModal/types";
import { checkedTargetsLength } from "../../../../Components/SplitCostModal/utils";
import { type MetadataOption } from "../../../../types";
import { AllocationPathType, getAllocationPath } from "../../allocations/utils";
import { useMetricSplitsContext } from "../../Context";

type Props = {
  open: boolean;
  anchorEl: Element | null;
  onClose: () => void;
  metricSplitModalClicked: () => void;
  selectedDimension?: MetadataOption;
  onOpenFilterDialog?: (event: SyntheticEvent) => void;
  onClearFilters?: (selectedItem: MetadataOption) => void;
  isInFilterResults?: boolean;
};

const text = {
  textOverflow: "ellipsis",
  maxWidth: "100%",
  overflow: "hidden",
  whiteSpace: "nowrap",
};

const DimensionItemMenu = ({
  open,
  anchorEl,
  onClose,
  metricSplitModalClicked,
  selectedDimension,
  onOpenFilterDialog,
  onClearFilters,
  isInFilterResults,
}: Props) => {
  const { metricSplitLabel, deleteSplit, editSplit, metricSplits } = useMetricSplitsContext();
  const customerId = useCustomerId();
  const splits = Object.values(metricSplits ?? []).filter((split) => split.id === selectedDimension?.id);

  const onDeleteClick = useCallback(
    (event: SyntheticEvent, split: Split) => {
      event.stopPropagation();
      deleteSplit(split);
    },
    [deleteSplit]
  );

  const handleFilter = useCallback(
    (event: SyntheticEvent) => {
      onOpenFilterDialog?.(event);
      onClose();
    },
    [onOpenFilterDialog, onClose]
  );

  const handleClearFilters = useCallback(() => {
    if (selectedDimension && onClearFilters) {
      onClearFilters(selectedDimension);
      onClose();
    }
  }, [selectedDimension, onClearFilters, onClose]);

  const handleViewAllocation = useCallback(() => {
    if (selectedDimension) {
      const allocationId = selectedDimension.id.split(":")[1];
      if (allocationId) {
        window.open(getAllocationPath(customerId, AllocationPathType.VIEW, allocationId), "_blank");
      }
    }
  }, [selectedDimension, customerId]);

  const lowerCaseMetricSplitLabel = (metricSplitLabel || "").toLowerCase();

  // Check if the dimension has filters to clear
  const hasFilters =
    selectedDimension &&
    ((selectedDimension._filter && selectedDimension._filter.length > 0) ||
      (selectedDimension._regexp && selectedDimension._regexp.length > 0));

  // Show clear filters option only for dimensions NOT in filter results and that have filters
  const showClearFilters = !isInFilterResults && hasFilters && onClearFilters;

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
      >
        {splits.length > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              px: 2,
              py: 0.75,
            }}
          >
            {metricSplittingTxt.SPLIT} {lowerCaseMetricSplitLabel}
          </Typography>
        )}

        {splits.map((split) => {
          const description = `${capitalize(split.mode)} ${
            split.mode !== "proportional" ? "split" : ""
          } between ${checkedTargetsLength(split.targets)} items`;
          return (
            <MenuItem
              sx={{ pr: 0.5 }}
              key={`split-menu-${split.origin?.id}`}
              onClick={() => {
                editSplit(split);
              }}
            >
              <Stack
                direction="row"
                spacing={3}
                sx={{
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Stack sx={{ maxWidth: "200px" }}>
                  <Typography variant="body1" sx={text}>
                    {split.origin?.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={[
                      {
                        color: "text.secondary",
                      },
                      ...(Array.isArray(text) ? text : [text]),
                    ]}
                  >
                    {description}
                  </Typography>
                </Stack>
                <IconButton
                  onClick={(event) => {
                    onDeleteClick(event, split);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </MenuItem>
          );
        })}
        {splits.length > 0 && <Divider />}
        {onOpenFilterDialog && (
          <MenuItem onClick={handleFilter} data-cy="split-menu-filter">
            {hasFilters ? reportText.EDIT_FILTERS_SECTION_LABEL : reportText.FILTER_RESULTS_SECTION_LABEL}
          </MenuItem>
        )}
        {showClearFilters && (
          <MenuItem onClick={handleClearFilters} data-cy="dimension-menu-clear-filters">
            {reportText.CLEAR_FILTERS_SECTION_LABEL}
          </MenuItem>
        )}
        <MenuItem onClick={metricSplitModalClicked}>
          {metricSplittingTxt.SPLIT} {lowerCaseMetricSplitLabel}
        </MenuItem>
        {selectedDimension?.data.type === Metadata.ATTRIBUTION_GROUP && (
          <MenuItem onClick={handleViewAllocation}>{reportText.VIEW_ALLOCATION_LABEL}</MenuItem>
        )}
      </Menu>
    </>
  );
};

export default DimensionItemMenu;
